/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ComponentType, MutableRefObject } from 'react';

import { HoverEffectWrapper, OverflowCarousel } from '@coursera-web/cds-labs';

import config from 'js/app/config';

import type { IconProps } from '@coursera/cds-common';
import { Grid, Link, Typography2, breakpoints } from '@coursera/cds-core';
import { AchievementIcon, ArrowNextIcon, CertificateIcon, DegreeIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { Maybe } from 'bundles/premium-hub/__generated__/baseContentfulTypes';
import type { ProductVariantCardsFragment } from 'bundles/premium-hub/__generated__/contentfulTypes';
import Container from 'bundles/premium-hub/components/shared/Container';
import Media from 'bundles/premium-hub/components/shared/Media';
import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';
import type { MediaAsset } from 'bundles/premium-hub/types';

import annularSectorImage from 'images/front-page/AnnularSector.svg';
import linesImage from 'images/front-page/Lines.png';

export type ProgramLevelModuleProps = {
  heading: string;
  description?: string;
  media?: MediaAsset;
  productVariantCards?: Array<Maybe<ProductVariantCardsFragment>>;
};

type ProgramLevelCard = ProductVariantCardsFragment & { title: string; callToActionLink: string };

const SECTION_NAME = 'program_level';

const CARD_ICONS: Array<ComponentType<IconProps>> = [AchievementIcon, DegreeIcon, CertificateIcon];

const styles = {
  moduleContainer: css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-400);
  `,
  panel: css`
    ${breakpoints.up('sm')} {
      background-color: var(--cds-color-neutral-background-primary);
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      border-radius: var(--cds-border-radius-300);
      padding: var(--cds-spacing-300);
    }
  `,
  content: css`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
  `,
  description: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  cardList: css`
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 1fr;
    gap: var(--cds-spacing-150);

    ${breakpoints.down('xs')} {
      width: 100%;
      grid-auto-flow: row;
      grid-template-columns: unset;
      grid-auto-columns: 1fr;
      grid-template-rows: none;
    }
  `,
  card: css`
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
    min-height: 70px;

    /* The right padding keeps the title and arrow clear of the decorative images and icon. */
    padding: 0 calc(var(--cds-spacing-100) * 11) 0 var(--cds-spacing-200);
    overflow: hidden;
    border-radius: var(--cds-border-radius-300);
    background-image:
      url(${linesImage}), url(${annularSectorImage}),
      linear-gradient(278deg, var(--cds-color-purple-25) 17.86%, var(--cds-color-blue-25) 75.22%);
    background-repeat: no-repeat, no-repeat, no-repeat;
    background-position:
      center right 28px,
      center right 14px,
      center;
    background-size:
      60px auto,
      auto 60px,
      cover;
    color: var(--cds-color-neutral-primary);
    text-decoration: none;
  `,
  cardLabel: css`
    display: flex;
    align-items: center;
  `,
  cardTitle: css`
    width: max-content;
  `,
  arrowIcon: css`
    color: var(--cds-color-neutral-primary);
  `,
  cardIcon: css`
    position: absolute;
    top: 50%;
    right: var(--cds-spacing-400);
    transform: translateY(-50%);
    pointer-events: none;
    width: 30px;
    height: 30px;
  `,
};

const ProgramLevelModule = ({ heading, description, media, productVariantCards }: ProgramLevelModuleProps) => {
  const trackV3 = useTracker();
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const sectionRef: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_page_section', {
    pageSection: { sectionName: SECTION_NAME, index: sectionIndex },
  });

  const cards = (productVariantCards ?? []).filter(
    (card): card is ProgramLevelCard => !!card?.title && !!card.callToActionLink
  );

  if (!heading && !cards.length) {
    return null;
  }

  const formatCardUrl = (url: string) => `${config.url.base}${url.replace(/^\//, '')}`;

  const trackCardClick = (linkName: string, linkURL: string) =>
    trackV3('click_button', {
      button: { name: 'degrees_level_hub', linkType: 'internal', linkName, linkURL },
      pageSection: { sectionName: SECTION_NAME, index: sectionIndex },
    });

  return (
    <Container css={styles.moduleContainer}>
      <div css={styles.panel} ref={sectionRef} data-testid="program-level-module">
        <Grid container spacing={24} alignItems="center">
          <Grid item xs={12} md={8} css={styles.content}>
            {heading && (
              <Typography2 variant="titleXSmall" component="h2">
                {heading}
              </Typography2>
            )}
            {description && (
              <Typography2 variant="bodyPrimary" component="p" css={styles.description}>
                {description}
              </Typography2>
            )}
            {cards.length > 0 && (
              <OverflowCarousel css={styles.cardList} shouldAllowSpaceForHoverItems>
                {cards.map(({ title, callToActionLink }, index) => {
                  const href = formatCardUrl(callToActionLink);
                  const iconIndex = index % CARD_ICONS.length;
                  const CardIcon = CARD_ICONS[iconIndex] ?? DegreeIcon;
                  return (
                    <HoverEffectWrapper
                      key={title}
                      component={Link}
                      href={href}
                      css={styles.card}
                      onClick={() => trackCardClick(title, href)}
                    >
                      <div css={styles.cardLabel}>
                        <Typography2 variant="actionPrimary" component="span" css={styles.cardTitle}>
                          {title}
                        </Typography2>
                        <ArrowNextIcon css={styles.arrowIcon} aria-hidden size="large" />
                      </div>
                      <CardIcon css={styles.cardIcon} aria-hidden />
                    </HoverEffectWrapper>
                  );
                })}
              </OverflowCarousel>
            )}
          </Grid>
          {media?.url && (
            <Grid item xs={12} md={4} data-testid="program-level-media">
              <Media
                heading={heading}
                media={media}
                dataTestIdPrefix="program-level"
                videoSettings={{ autoplay: true, muted: true, sentryErrorSource: 'ProgramLevelModule' }}
              />
            </Grid>
          )}
        </Grid>
      </div>
    </Container>
  );
};

export default ProgramLevelModule;
